import type { ResolvedBrandPaletteModel } from '@supply-mover-2-0/shared';
import { BRAND_LIGHT_TEXT, readableTextOn } from '@supply-mover-2-0/shared';

/**
 * What sits under the native status bar (PD-16744).
 *
 * The storefront app draws its WebView under the iOS status bar
 * (`setOverlaysWebView` in main.tsx), so the status bar's text has to read
 * against whatever the current page paints at the top of the screen.
 *
 *   - `header`: the brand header band (PortalHeader) — the brand primary in
 *     light mode, the neutral header in dark mode. The default, because every
 *     PortalLayout page shows the band.
 *   - `page`: a page with no header band that paints a mode neutral at the top
 *     (`bg-brand-page-bg` or `bg-surface`): light in light mode, dark in dark
 *     mode. Account deleted, Select Account, the password pages and the
 *     sign-in link page, plus Register and the welcome screen on a brand with
 *     a light login theme.
 */
export type StatusBarSurface = 'header' | 'page';

/** The color of the status bar's own text: the clock, battery and signal. */
export type StatusBarText = 'light' | 'dark';

/**
 * The `Style` member of `@capacitor/status-bar` that produces a given text
 * color. Capacitor names the style after the BACKGROUND it suits, so the
 * names read inverted: `Style.Dark` gives LIGHT text and `Style.Light` gives
 * DARK text. Kept as the member name (not the enum value) so this module
 * does not import the plugin, which only loads inside the native shell.
 */
export type CapacitorStatusBarStyleName = 'Dark' | 'Light';

/**
 * The color behind the status bar for a surface. Both neutrals a page can
 * paint (`--brand-page-bg` and `--bg-surface`) are light in light mode and
 * dark in dark mode, so the page neutral stands for either of them.
 */
export function statusBarBackground(
	surface: StatusBarSurface,
	palette: Pick<ResolvedBrandPaletteModel, 'header' | 'neutrals'>,
): string {
	return surface === 'page' ? palette.neutrals.page : palette.header;
}

/** The readable status-bar text color on a surface. */
export function statusBarTextFor(
	surface: StatusBarSurface,
	palette: Pick<ResolvedBrandPaletteModel, 'header' | 'neutrals'>,
): StatusBarText {
	// A color neither white nor near-black can read on falls back to white,
	// the same fallback the header label uses (`readableTextRgbOn`).
	const text =
		readableTextOn(statusBarBackground(surface, palette)) ?? BRAND_LIGHT_TEXT;
	return text === BRAND_LIGHT_TEXT ? 'light' : 'dark';
}

/** The Capacitor style that gives the requested text color. */
export function capacitorStatusBarStyleFor(
	text: StatusBarText,
): CapacitorStatusBarStyleName {
	return text === 'light' ? 'Dark' : 'Light';
}
